import { FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from '../../../hooks/locale'

import BreadcrumbSection from '../../landing-page/breadcrumb-section'
import { useAsyncCallback } from '../../../hooks/async'
import { BreadcrumbList } from '../../../models/common/breadcrumb-model'
import breadcrumbClient from '../../../clients/breadcrumb-client'
import AdvancedBreadcrumbTransport from '../../../transports/common/advanced-breadcrumb-transport'
import { useRequestUserConfigContext } from '../../../hooks/request'
import BreadcrumbSkeleton from './breadcrumb-skeleton'
import { RequestUserConfig } from '../../../common/types'
import { useSessionEffect } from '../../../hooks/session'

interface Props {
    url: string
    exposeLabel?: string
    hideMobile?: boolean
    containerPadding?: string
}

const _getBreadcrumbs = async (user: RequestUserConfig, url: string) => {
    const response = await breadcrumbClient.getBreadcrumbs({ user, url })

    return response.data
}

const BreadcrumbWrapper: FC<Props> = ({ url, exposeLabel = '', hideMobile, containerPadding }) => {
    const { t } = useTranslation()
    const user = useRequestUserConfigContext()
    const breadcrumbList = useMemo(() => new BreadcrumbList(t), [t])

    const fetchBreadcrumbs = useCallback(async () => _getBreadcrumbs(user, url), [user, url])

    const {
        data: breadcrumbs,
        isLoading,
        callback,
    } = useAsyncCallback<AdvancedBreadcrumbTransport[]>(fetchBreadcrumbs, null)

    useSessionEffect(() => {
        callback()
    }, [callback])

    const formattedBreadcrumbs = useMemo(() => {
        if (!breadcrumbs) {
            return []
        }
        return breadcrumbList.format(breadcrumbs, exposeLabel)
    }, [breadcrumbs, breadcrumbList, exposeLabel])

    if (isLoading) {
        return <BreadcrumbSkeleton containerPadding={containerPadding} />
    }

    if (formattedBreadcrumbs.length === 0) {
        return null
    }

    return (
        <BreadcrumbSection
            breadcrumbs={formattedBreadcrumbs}
            url={url}
            hideMobile={hideMobile}
            containerPadding={containerPadding}
        />
    )
}

export default memo(BreadcrumbWrapper)
